/**
 * Diálogo da OS (spec 016 US1/US2): abre com o rascunho lido do ticket (ficha
 * na descrição + título) ou com a OS existente, o atendente revisa,
 * escolhe data/período e técnico pela disponibilidade do dia, e salva como
 * rascunho ou já agendada. "Redigir instruções com IA" só aparece com a
 * integração ativa, e o texto volta editável.
 */
import { useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Sparkles } from 'lucide-react';
import {
  AI_REQUEST_TIMEOUT_MS,
  SERVICE_ORDER_PERIOD_LABELS,
  SERVICE_ORDER_PERIODS,
  SERVICE_ORDER_TYPE_LABELS,
  SERVICE_ORDER_TYPES,
  type AiText,
  type ServiceOrder,
  type ServiceOrderDraft,
  type ServiceOrderPeriod,
  type ServiceOrderType,
  type TechnicianAvailability,
} from '@erp/shared';
import { api } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input, Label, Select, Textarea } from '@/components/ui/input';
import { Badge, Dialog, Skeleton, Spinner } from '@/components/ui/misc';
import { cn } from '@/lib/utils';
import { useAiActive } from '@/features/tickets/ai-actions';
import { capitalize, useTerms } from '@/shared/tenant-profile';
import {
  SERVICE_ORDERS_KEY,
  serviceOrderErrorMessage,
  technicianAvailabilityQuery,
  TECHNICIANS_KEY,
} from './service-orders-queries';
import {
  TechnicianPicker as SearchTechnicianPicker,
  type TechnicianRefValue,
} from './TechnicianPicker';

/** Campos de texto da OS, na ordem da tela. */
const TEXT_FIELDS = [
  'contactName',
  'contactPhones',
  'vehiclePlate',
  'vehicleBrand',
  'vehicleModel',
  'vehicleColor',
  'vehicleYear',
  'vehicleChassis',
  'addressStreet',
  'addressNumber',
  'addressComplement',
  'addressNeighborhood',
  'addressCity',
  'addressState',
  'addressZip',
  'instructions',
] as const;
type TextField = (typeof TEXT_FIELDS)[number];

interface FormState extends Record<TextField, string> {
  type: ServiceOrderType;
  technicianId: number | null;
  scheduledDate: string;
  scheduledPeriod: ServiceOrderPeriod | '';
}

function toForm(src: ServiceOrderDraft | ServiceOrder): FormState {
  const text = Object.fromEntries(TEXT_FIELDS.map((k) => [k, src[k] ?? ''])) as Record<TextField, string>;
  const existing = 'status' in src ? src : null;
  return {
    ...text,
    type: src.type,
    technicianId: existing?.technician?.id ?? null,
    scheduledDate: existing?.scheduledDate ?? '',
    scheduledPeriod: existing?.scheduledPeriod ?? '',
  };
}

const AI_CLIENT_TIMEOUT_MS = AI_REQUEST_TIMEOUT_MS + 15_000;

export function ServiceOrderDialog({
  ticketNumber,
  source,
  onClose,
}: {
  ticketNumber: number;
  /** rascunho (criar) ou OS existente (editar) */
  source: ServiceOrderDraft | ServiceOrder;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const existing = 'status' in source ? source : null;
  const [form, setForm] = useState<FormState>(() => toForm(source));
  const aiActive = useAiActive();
  // spec 017: rótulos do contato e do recurso no vocabulário do tenant
  const terms = useTerms();
  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const availabilityQ = useQuery(technicianAvailabilityQuery(form.scheduledDate, form.addressCity));
  const selected = availabilityQ.data?.find((r) => r.technician.id === form.technicianId) ?? null;

  const save = useMutation({
    mutationFn: async (status: 'draft' | 'scheduled' | null) => {
      const body = {
        ...Object.fromEntries(TEXT_FIELDS.map((k) => [k, form[k]])),
        type: form.type,
        technicianId: form.technicianId,
        scheduledDate: form.scheduledDate || null,
        scheduledPeriod: form.scheduledPeriod || null,
        ...(status ? { status } : {}),
      };
      const res = existing
        ? await api<ServiceOrder>(`/tickets/${ticketNumber}/service-orders/${existing.id}`, {
            method: 'PATCH',
            body,
          })
        : await api<ServiceOrder>(`/tickets/${ticketNumber}/service-orders`, { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SERVICE_ORDERS_KEY });
      // a carga do dia mudou
      void qc.invalidateQueries({ queryKey: TECHNICIANS_KEY });
      // a timeline ganhou service_order_created
      void qc.invalidateQueries({ queryKey: ['erp', 'tickets'] });
      onClose();
    },
  });

  const ai = useMutation({
    mutationFn: async () => {
      const res = await api<AiText>(`/tickets/${ticketNumber}/service-orders/ai-instructions`, {
        method: 'POST',
        timeoutMs: AI_CLIENT_TIMEOUT_MS,
      });
      if (res.error) throw res.error;
      return res.data.text;
    },
    // não apaga o que o atendente já escreveu: acrescenta abaixo
    onSuccess: (text) =>
      setForm((f) => ({
        ...f,
        instructions: f.instructions.trim() ? `${f.instructions.trim()}\n\n${text}` : text,
      })),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate(existing ? null : 'draft');
  };
  const canSchedule = form.technicianId !== null && form.scheduledDate !== '';

  return (
    <Dialog
      open
      onClose={onClose}
      className="sm:max-w-3xl"
      title={existing ? `Editar OS nº ${existing.number}` : `Gerar OS — ticket #${ticketNumber}`}
    >
      <form onSubmit={submit} className="space-y-5">
        {!existing && 'sgrRecognized' in source ? (
          <p
            className={cn(
              'rounded-md border px-3 py-2 text-sm',
              source.sgrRecognized
                ? 'border-primary/30 bg-primary/5 text-foreground'
                : 'border-[#FFE58F] bg-[#FFFBE6] text-[#874D00]'
            )}
          >
            {source.sgrRecognized
              ? `Preenchido a partir da ficha do ticket (mesa ${source.deskName}). Revise antes de salvar.`
              : 'O ticket não tem ficha — preencha os campos à mão.'}
          </p>
        ) : null}

        <Section title="Serviço">
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Tipo" htmlFor="os-type">
              <Select id="os-type" value={form.type} onChange={(e) => set('type', e.target.value as ServiceOrderType)}>
                {SERVICE_ORDER_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {SERVICE_ORDER_TYPE_LABELS[t]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Data" htmlFor="os-date">
              <Input id="os-date" type="date" value={form.scheduledDate} onChange={(e) => set('scheduledDate', e.target.value)} />
            </Field>
            <Field label="Período" htmlFor="os-period">
              <Select
                id="os-period"
                value={form.scheduledPeriod}
                onChange={(e) => set('scheduledPeriod', e.target.value as ServiceOrderPeriod | '')}
              >
                <option value="">—</option>
                {SERVICE_ORDER_PERIODS.map((p) => (
                  <option key={p} value={p}>
                    {SERVICE_ORDER_PERIOD_LABELS[p]}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
        </Section>

        <Section title="Técnico">
          <TechnicianPicker
            date={form.scheduledDate}
            city={form.addressCity}
            value={form.technicianId}
            currentName={existing?.technician?.name ?? null}
            rows={availabilityQ.data}
            loading={availabilityQ.isFetching && !availabilityQ.data}
            onChange={(id) => set('technicianId', id)}
          />
          {selected?.full ? (
            <p className="mt-2 flex items-center gap-1.5 text-sm text-[#D48806]">
              <AlertTriangle className="size-4 shrink-0" />
              {selected.technician.name} já tem {selected.scheduledCount} OS nesse dia (capacidade{' '}
              {selected.technician.dailyCapacity}). Dá para agendar mesmo assim.
            </p>
          ) : null}
        </Section>

        <Section title="Contato">
          <div className="grid gap-3 sm:grid-cols-2">
            <TextInput form={form} set={set} field="contactName" label={`Nome do ${terms.contact}`} />
            <TextInput form={form} set={set} field="contactPhones" label="Telefones" />
          </div>
        </Section>

        <Section title={capitalize(terms.resource)}>
          <div className="grid gap-3 sm:grid-cols-3">
            <TextInput form={form} set={set} field="vehiclePlate" label={capitalize(terms.resourceId)} />
            <TextInput form={form} set={set} field="vehicleBrand" label="Marca" />
            <TextInput form={form} set={set} field="vehicleModel" label="Modelo" />
            <TextInput form={form} set={set} field="vehicleColor" label="Cor" />
            <TextInput form={form} set={set} field="vehicleYear" label="Ano" />
            <TextInput form={form} set={set} field="vehicleChassis" label="Chassi" />
          </div>
        </Section>

        <Section title="Endereço">
          <div className="grid gap-3 sm:grid-cols-6">
            <div className="sm:col-span-4">
              <TextInput form={form} set={set} field="addressStreet" label="Logradouro" />
            </div>
            <div className="sm:col-span-2">
              <TextInput form={form} set={set} field="addressNumber" label="Número" />
            </div>
            <div className="sm:col-span-3">
              <TextInput form={form} set={set} field="addressComplement" label="Complemento" />
            </div>
            <div className="sm:col-span-3">
              <TextInput form={form} set={set} field="addressNeighborhood" label="Bairro" />
            </div>
            <div className="sm:col-span-3">
              <TextInput form={form} set={set} field="addressCity" label="Cidade" />
            </div>
            <div className="sm:col-span-1">
              <TextInput form={form} set={set} field="addressState" label="UF" />
            </div>
            <div className="sm:col-span-2">
              <TextInput form={form} set={set} field="addressZip" label="CEP" />
            </div>
          </div>
        </Section>

        <Section
          title="Instruções"
          action={
            aiActive ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={ai.isPending}
                onClick={() => ai.mutate()}
              >
                {ai.isPending ? <Spinner className="size-4" /> : <Sparkles />}
                Redigir instruções com IA
              </Button>
            ) : null
          }
        >
          <Textarea
            id="os-instructions"
            aria-label="Instruções"
            rows={5}
            value={form.instructions}
            onChange={(e) => set('instructions', e.target.value)}
            placeholder="O que o técnico precisa saber para executar o serviço."
          />
          {ai.error ? (
            <p className="mt-1 text-sm text-destructive">{serviceOrderErrorMessage(ai.error)}</p>
          ) : null}
        </Section>

        {save.error ? (
          <p className="text-sm text-destructive" role="alert">
            {serviceOrderErrorMessage(save.error)}
          </p>
        ) : null}
        <div className="flex flex-wrap justify-end gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          {existing ? (
            <Button type="submit" size="sm" disabled={save.isPending}>
              {save.isPending ? 'Salvando…' : 'Salvar'}
            </Button>
          ) : (
            <>
              <Button type="submit" variant="outline" size="sm" disabled={save.isPending}>
                Salvar rascunho
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={save.isPending || !canSchedule || selected?.absent === true}
                title={canSchedule ? undefined : 'Escolha técnico e data para agendar'}
                onClick={() => save.mutate('scheduled')}
              >
                {save.isPending ? 'Salvando…' : 'Salvar e agendar'}
              </Button>
            </>
          )}
        </div>
      </form>
    </Dialog>
  );
}

function Section({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">{title}</h3>
        {action}
      </div>
      {children}
    </section>
  );
}

function Field({ label, htmlFor, children }: { label: string; htmlFor: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
    </div>
  );
}

function TextInput({
  form,
  set,
  field,
  label,
}: {
  form: FormState;
  set: <K extends keyof FormState>(key: K, value: FormState[K]) => void;
  field: TextField;
  label: string;
}) {
  const id = `os-${field}`;
  return (
    <Field label={label} htmlFor={id}>
      <Input id={id} value={form[field]} onChange={(e) => set(field, e.target.value)} />
    </Field>
  );
}

/**
 * Escolha do técnico (US2). Com data: a lista da disponibilidade (quem atende
 * a cidade primeiro; "mostrar todos" revela os de outra cidade), com carga do
 * dia, agenda cheia (escolhível com aviso) e ausente (bloqueado). Sem data:
 * só os técnicos ativos, sem carga — a OS pode ficar em rascunho sem dia.
 */
function TechnicianPicker({
  date,
  city,
  value,
  currentName,
  rows,
  loading,
  onChange,
}: {
  date: string;
  city: string;
  value: number | null;
  currentName: string | null;
  rows: TechnicianAvailability[] | undefined;
  loading: boolean;
  onChange: (id: number | null) => void;
}) {
  const [showAll, setShowAll] = useState(false);
  // o nome do escolhido pela busca: o formulário guarda só o id
  const [picked, setPicked] = useState<TechnicianRefValue | null>(null);

  if (!date) {
    // spec 066 (D3): ~1.600 ativos do SGR — sem data, é a busca do servidor
    // (nome, cidade ou região), e não uma lista com todos
    const current: TechnicianRefValue | null =
      value === null
        ? null
        : picked?.id === value
          ? picked
          : { id: value, name: currentName ?? `Técnico #${value}` };
    return (
      <div className="space-y-1.5" data-tour="os-tecnico-busca">
        <SearchTechnicianPicker
          value={current}
          onChange={(id, technician) => {
            setPicked(technician);
            onChange(id);
          }}
        />
        <p className="text-xs text-muted-foreground">Escolha a data para ver a disponibilidade do dia.</p>
      </div>
    );
  }

  if (loading) return <Skeleton className="h-24 w-full" />;
  const all = rows ?? [];
  const visible = showAll ? all : all.filter((r) => r.coversCity !== false);
  const hiddenCount = all.length - visible.length;
  // técnico atual que não está mais na lista (desativado): continua visível
  const orphan = value !== null && !all.some((r) => r.technician.id === value);

  return (
    <div className="space-y-2">
      <div className="max-h-64 space-y-1 overflow-y-auto rounded-md border p-1" role="radiogroup" aria-label="Técnico">
        <TechOption selected={value === null} onSelect={() => onChange(null)} name="Sem técnico" />
        {orphan ? (
          <TechOption selected onSelect={() => {}} name={currentName ?? `Técnico #${value}`}>
            <Badge>desativado</Badge>
          </TechOption>
        ) : null}
        {visible.length === 0 ? (
          <p className="px-3 py-2 text-sm text-muted-foreground">
            {all.length === 0 ? 'Nenhum técnico ativo cadastrado.' : 'Ninguém atende esta cidade.'}
          </p>
        ) : (
          visible.map((r) => (
            <TechOption
              key={r.technician.id}
              selected={value === r.technician.id}
              disabled={r.absent}
              onSelect={() => onChange(r.technician.id)}
              name={r.technician.name}
            >
              <span className="text-xs text-muted-foreground tabular-nums">
                {r.scheduledCount}/{r.technician.dailyCapacity} no dia
              </span>
              {r.coversCity === false ? <Badge>outra cidade</Badge> : null}
              {r.absent ? (
                <Badge variant="destructive" title={r.absenceReason ?? undefined}>
                  ausente
                </Badge>
              ) : r.full ? (
                <Badge variant="warning">agenda cheia</Badge>
              ) : (
                <Badge variant="success">disponível</Badge>
              )}
            </TechOption>
          ))
        )}
      </div>
      {hiddenCount > 0 || showAll ? (
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <Checkbox checked={showAll} onCheckedChange={(v) => setShowAll(v === true)} />
          Mostrar todos{hiddenCount > 0 ? ` (+${hiddenCount} de outra cidade)` : ''}
        </label>
      ) : null}
      {!city.trim() ? (
        <p className="text-xs text-muted-foreground">Sem cidade na OS: a lista não filtra por região.</p>
      ) : null}
    </div>
  );
}

function TechOption({
  selected,
  disabled,
  onSelect,
  name,
  children,
}: {
  selected: boolean;
  disabled?: boolean;
  onSelect: () => void;
  name: string;
  children?: ReactNode;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      disabled={disabled}
      onClick={onSelect}
      className={cn(
        'flex w-full items-center gap-2 rounded px-3 py-1.5 text-left text-sm transition-colors',
        selected ? 'bg-primary/10 font-medium text-foreground' : 'hover:bg-accent',
        disabled && 'cursor-not-allowed opacity-50 hover:bg-transparent'
      )}
    >
      <span
        className={cn(
          'size-3.5 shrink-0 rounded-full border',
          selected ? 'border-4 border-primary' : 'border-muted-foreground/40'
        )}
        aria-hidden
      />
      <span className="flex-1 truncate">{name}</span>
      {children}
    </button>
  );
}
